import type { ProductScanButtonsProps } from '@/core/components/products';
import {
  ProductHashChip,
  ProductScanButtons,
} from '@/core/components/products';
import { ProductManualCodeEntry } from '@/core/components/products/product-manual-code-entry';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { ProductsSetupStepShell } from '@/routes/app/products-setup-wizard/products-setup-step-shell';

export type ProductsSetupStepScanScanProps = Omit<
  ProductScanButtonsProps,
  'scanQrLabel' | 'scanNfcLabel'
>;

export interface ProductsSetupStepScanProps {
  hashes: string[];
  removeHash: (index: number) => void;
  error: string | null;
  scanProps: ProductsSetupStepScanScanProps;
  /** Shown when the scanned code needs claim / blocked flow before setup. */
  outcomeSlot?: React.ReactNode;
  /** Typed code / sticker link as fallback to the camera (DEV-553). */
  manualEntry: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (result: { hash: string; productType?: string }) => void;
  };
}

export function ProductsSetupStepScan({
  hashes,
  removeHash,
  error,
  scanProps,
  outcomeSlot,
  manualEntry,
}: ProductsSetupStepScanProps) {
  const dict = useDictionary();
  const d =
    (dict as { products?: { setup?: Record<string, string> } })?.products
      ?.setup ?? {};

  const hasCodes = hashes.length > 0;
  const title = hasCodes
    ? (d.step1TitleWithCodes ?? d.step1Title ?? '')
    : (d.step1Title ?? '');
  // No NFC in the browser: don't ask for it there.
  const description = hasCodes
    ? (d.step1DescriptionWithCodes ?? d.step1Description)
    : scanProps.showNfc
      ? d.step1Description
      : (d.step1DescriptionNoNfc ?? d.step1Description);
  const scanQrLabel = hasCodes
    ? (d.scanAnotherQr ?? d.addAnother ?? d.scanQr ?? '')
    : (d.scanQr ?? '');
  const scanNfcLabel = hasCodes
    ? (d.scanAnotherNfc ?? d.scanNfc ?? '')
    : (d.scanNfc ?? '');

  return (
    <ProductsSetupStepShell title={title} description={description}>
      {hasCodes ? (
        <div className="flex flex-col gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
            {hashes.length === 1
              ? (d.productCodeReady ?? d.productCodesAdded)
              : d.productCodesAdded}
          </p>
          <ul className="flex flex-col gap-2">
            {hashes.map((h, index) => (
              <li key={`${h}-${index}`}>
                <ProductHashChip
                  hash={h}
                  index={index}
                  onRemove={removeHash}
                  removeAriaLabel={d.removeCode}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        {hasCodes ? (
          <p className="text-[13.5px] font-medium leading-relaxed text-foreground">
            {d.addMoreProductsTitle ?? d.addAnother}
          </p>
        ) : null}
        <ProductScanButtons
          {...scanProps}
          scanQrLabel={scanQrLabel}
          scanNfcLabel={scanNfcLabel}
          buttonVariant={hasCodes ? 'dashed' : 'outline'}
        />
        <ProductManualCodeEntry
          labels={{
            toggle: d.enterManually ?? '',
            label: d.manualCodeLabel ?? d.hashPlaceholder ?? '',
            placeholder: d.manualCodePlaceholder ?? '',
            submit: d.useCode ?? '',
            invalid: d.scanInvalidCode ?? '',
          }}
          open={manualEntry.open}
          onOpenChange={manualEntry.onOpenChange}
          onSubmit={manualEntry.onSubmit}
          disabled={scanProps.disabled}
        />
        {hasCodes ? (
          <p className="text-[13.5px] leading-relaxed text-muted-foreground">
            {d.addAnotherHint}
          </p>
        ) : null}
      </div>

      {error ? (
        <p className="text-[13.5px] leading-relaxed text-destructive">
          {error}
        </p>
      ) : null}

      {outcomeSlot}
    </ProductsSetupStepShell>
  );
}
